import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useState} from 'react';
import _ from 'underscore';

import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GREEN, RED} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import SecureTextInput from 'ui-library/inputs/SecureTextInput';
import TextField from 'ui-library/inputs/TextField';

import {
  ImportInstrumentDataModalAction,
  ImportInstrumentDataModalActionType,
} from './importInstrumentDataModalReducer';
import {ImportInstrumentDataModalState, InstrumentQueryStatus} from './InstrumentDataConstants';
import {createBrowseFilesInstrumentQuery, pollInstrumentQueryStatus} from './InstrumentDataDAO';

export interface InstrumentLoginPageProps {
  connectionId: string;
  dispatch: Dispatch<ImportInstrumentDataModalAction>;
  state: ImportInstrumentDataModalState;
}

async function submitBrowseFiles(
  username: string,
  password: string,
  connectionId: string
): Promise<InstrumentQueryStatus> {
  const instrumentQueryId = (await createBrowseFilesInstrumentQuery(username, password, connectionId))
    .instrumentQueryId;

  const query = await pollInstrumentQueryStatus(instrumentQueryId);
  return query.status;
}

export default function InstrumentLoginPage(props: InstrumentLoginPageProps): JSX.Element {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  return (
    <div>
      <Row>
        <Col sm={6}>
          <TextField
            name="Username"
            label="Username"
            labelStyle="label"
            isRequired={true}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
            }}
            disabled={false}
          />
        </Col>
      </Row>
      <Row className={css(styles.rowRemoveMarginBottom)}>
        <Col sm={6}>
          <FieldLabel
            htmlFor={_.uniqueId('fieldLabel-')}
            isRequired={true}
            label="Password "
            labelStyle="label"
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <SecureTextInput name="Password" value={password} onChange={setPassword} disabled={false} />
        </Col>
        <Col>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-right"
            size={ButtonSize.SMALL}
            text={props.state.isLoggingIn ? 'Validating credentials...' : 'Validate credentials'}
            disabled={
              props.state.loginQueryStatus === InstrumentQueryStatus.DONE ||
              props.state.isLoggingIn ||
              !(username.length > 0 && password.length > 0)
            }
            onClick={async () => {
              props.dispatch({
                type: ImportInstrumentDataModalActionType.IS_LOGGING_IN,
                isLoggingIn: true,
              });
              const loginQueryStatus = await submitBrowseFiles(username, password, props.connectionId);
              props.dispatch({
                type: ImportInstrumentDataModalActionType.LOGIN,
                loginQueryStatus,
                loginUsername: username,
              });
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          {props.state.loginQueryStatus !== InstrumentQueryStatus.PENDING &&
            (props.state.loginQueryStatus === InstrumentQueryStatus.DONE ? (
              <div className={css(styles.validCredentials)}>Credentials entered successfully.</div>
            ) : (
              <div className={css(styles.invalidCredentials)}>Invalid username or password.</div>
            ))}
        </Col>
      </Row>
    </div>
  );
}

export const styles = StyleSheet.create({
  invalidCredentials: {
    color: RED,
    fontStyle: 'italic',
  },
  rowRemoveMarginBottom: {
    marginBottom: 0,
  },
  validCredentials: {
    color: GREEN,
    fontStyle: 'italic',
  },
});
